/** GTsetu Expert Account Questionnaire — fixed onboarding form for newly promoted experts. */

export type ExpertAccountQuestionType =
  | "multiple"
  | "multiple_max_3"
  | "single"
  | "countries"
  | "short_text"
  | "agreement";

export type ExpertAccountQuestionOption = {
  id: string;
  label: string;
  allowsOtherText?: boolean;
};

export type ExpertAccountQuestion = {
  id: string;
  number: number;
  label: string;
  hint?: string;
  type: ExpertAccountQuestionType;
  options?: ExpertAccountQuestionOption[];
  maxLength?: number;
  maxCountries?: number;
  /** When false, answer is stored but not shown on the public Expertise profile tab. */
  publicOnProfile: boolean;
  required: boolean;
};

/** Short labels for the public expert profile (not questionnaire wording). */
export const EXPERT_ACCOUNT_PROFILE_FIELD_LABELS: Record<string, string> = {
  q1_expertise_types: "Expertise Offered",
  q2_countries: "Countries or Regions Supported",
  q3_market_support: "Market Support Direction",
  q4_sectors: "Sector Experience",
  q5_services: "Services Offered",
  q6_company_types: "Best Suited Company Types",
  q7_support_stages: "Stage of Company Support",
  q8_years_experience: "Years of Professional Experience",
  q9_cross_border: "Cross-Border Business Experience",
  q10_examples: "Relevant Experience Examples",
  q11_network_access: "Network Access",
  q12_languages: "Business Languages Supported",
  q13_support_preference: "Preferred Support Format",
  q14_availability: "Availability for Expert Requests",
  q15_documents: "Documents Open for Review",
  q16_output_types: "Possible Expert Output",
  q17_company_intents: "Relevant Company Intents",
  q18_company_sizes: "Company Size Usually Supported",
  q19_confidentiality: "Comfort with Confidential Information Review",
  q20_services_excluded: "Services Not Provided",
  q23_exclusions: "Sectors, Products, or Geographies Not Supported",
  q24_agreement: "Profile Confirmation",
};

export function getExpertProfileFieldLabel(question: ExpertAccountQuestion): string {
  return EXPERT_ACCOUNT_PROFILE_FIELD_LABELS[question.id] ?? question.label;
}

export function formatExpertiseProfileValue(displayValues: string[]): string {
  if (displayValues.length === 0) return "";
  if (displayValues.length === 1) return displayValues[0];
  return displayValues.join(" / ");
}

export type ExpertAccountAnswers = Record<string, string | string[]>;

export const EXPERT_ACCOUNT_QUESTIONNAIRE_INTRO =
  "This questionnaire helps us understand your expertise, geography, sector focus, services, and the type of companies you can support. The information provided will help companies understand your services better and allow GTsetu to match you with relevant collaboration opportunities.";

export const EXPERT_ACCOUNT_AGREEMENT_QUESTION_ID = "q24_agreement";
export const EXPERT_ACCOUNT_AGREEMENT_AGREE_VALUE = "agree";
export const EXPERT_ACCOUNT_AGREEMENT_DISAGREE_VALUE = "disagree";
export const EXPERT_ACCOUNT_QUESTIONNAIRE_REQUIRED_CODE = "EXPERT_ACCOUNT_QUESTIONNAIRE_REQUIRED";

/** HeadExpertise gate — same questionnaire as Expert, separate error code. */
export const HEAD_EXPERTISE_QUESTIONNAIRE_REQUIRED_CODE = "HEAD_EXPERTISE_QUESTIONNAIRE_REQUIRED";

const opt = (id: string, label: string, allowsOtherText = false): ExpertAccountQuestionOption => ({
  id,
  label,
  ...(allowsOtherText ? { allowsOtherText: true } : {}),
});

export const EXPERT_ACCOUNT_QUESTIONS: ExpertAccountQuestion[] = [
  {
    id: "q1_expertise_types",
    number: 1,
    label: "What kind of expertise would you like to provide on GTsetu?",
    hint: "Multiple options allowed",
    type: "multiple",
    publicOnProfile: true,
    required: true,
    options: [
      opt("market_entry", "Market entry"),
      opt("industry_technical", "Industry and Technical Consultants"),
      opt("trade_supply_chain", "Trade & Supply Chain Consultants"),
      opt("regulatory_compliance", "Regulatory and Compliance"),
      opt("legal_tax", "Legal and Tax Advisory"),
    ],
  },
  {
    id: "q2_countries",
    number: 2,
    label: "Which countries or regions can you support companies in?",
    hint: "Up to 5 countries or regions",
    type: "countries",
    maxCountries: 5,
    publicOnProfile: true,
    required: true,
  },
  {
    id: "q3_market_support",
    number: 3,
    label:
      "Are you able to support companies entering this market, exporting from this market, or both?",
    type: "single",
    publicOnProfile: true,
    required: true,
    options: [
      opt("entering", "Companies entering this geography"),
      opt("exporting", "Companies exporting from this geography"),
      opt("both", "Both"),
    ],
  },
  {
    id: "q4_sectors",
    number: 4,
    label: "Which sectors do you have experience in?",
    hint: "Up to 3 options",
    type: "multiple_max_3",
    publicOnProfile: true,
    required: true,
    options: [
      opt("manufacturing_automation", "Manufacturing & Industrial Automation"),
      opt("automotive_mobility", "Automotive & Mobility"),
      opt("energy_utilities", "Energy & Utilities"),
      opt("renewable_energy", "Renewable Energy"),
      opt("electronics_technology", "Electronics & Technology"),
      opt("chemicals_materials", "Chemicals & Materials"),
      opt("mining_metals", "Mining & Metals"),
      opt("healthcare_life_sciences", "Healthcare & Life Sciences"),
      opt("agriculture_food", "Agriculture & Food Systems"),
      opt("fmcg_food_beverage", "FMCG / Food & Beverage"),
      opt("infrastructure_construction", "Infrastructure & Construction"),
      opt("textile_apparel", "Textile & Apparel"),
      opt("logistics_supply_chain", "Logistics & Supply Chain"),
      opt("aerospace_space", "Aerospace / Space Tech"),
      opt("maritime_ports", "Maritime / Ports"),
      opt("other", "Other", true),
    ],
  },
  {
    id: "q5_services",
    number: 5,
    label: "What specific services can companies request from you?",
    hint: "Multiple options allowed",
    type: "multiple",
    publicOnProfile: true,
    required: true,
    options: [
      opt("market_understanding", "Market understanding / opportunity assessment"),
      opt("partner_identification", "Partner identification"),
      opt("distributor_identification", "Distributor identification"),
      opt("buyer_supplier_shortlisting", "Buyer / supplier shortlisting"),
      opt("local_culture_guidance", "Local business culture guidance"),
      opt("regulatory_review", "Regulatory requirement review"),
      opt("certification_guidance", "Certification requirement guidance"),
      opt("product_market_fit", "Product-market fit assessment"),
      opt("pricing_positioning", "Pricing / positioning guidance"),
      opt("competitor_landscape", "Competitor / market landscape review"),
      opt("introduction_support", "Introduction support"),
      opt("due_diligence", "Due diligence support"),
      opt("documentation_review", "Documentation review"),
      opt("gtm_strategy", "Go-to-market strategy"),
      opt("sales_channel_strategy", "Sales channel strategy"),
      opt("export_readiness", "Export readiness assessment"),
      opt("local_representation", "Local representation guidance"),
      opt("meeting_preparation", "Meeting preparation"),
      opt("negotiation_support", "Negotiation support"),
      opt("post_intro_advisory", "Post-introduction advisory"),
      opt("other", "Other", true),
    ],
  },
  {
    id: "q6_company_types",
    number: 6,
    label: "What type of companies are best suited to approach you?",
    hint: "Up to 3 options",
    type: "multiple_max_3",
    publicOnProfile: true,
    required: true,
    options: [
      opt("manufacturers", "Manufacturers"),
      opt("exporters", "Exporters"),
      opt("importers", "Importers"),
      opt("distributors", "Distributors"),
      opt("technology_providers", "Technology Providers"),
      opt("service_providers", "Service Providers"),
      opt("startups", "Startups"),
      opt("smes_msme", "SMEs / MSMEs"),
      opt("mid_sized", "Mid-sized Companies"),
      opt("large_enterprises", "Large Enterprises"),
      opt("chambers_associations", "Chambers / Associations"),
      opt("investors", "Investors"),
      opt("government_public", "Government / Public-sector Bodies"),
      opt("other", "Other", true),
    ],
  },
  {
    id: "q7_support_stages",
    number: 7,
    label: "At what stage can you support companies?",
    hint: "Up to 3 options",
    type: "multiple_max_3",
    publicOnProfile: true,
    required: true,
    options: [
      opt("early_exploration", "Early market exploration"),
      opt("market_entry_planning", "Market entry planning"),
      opt("finding_partners", "Finding local partners"),
      opt("finding_distributors", "Finding distributors / agents"),
      opt("product_localization", "Product localization"),
      opt("regulatory_preparation", "Regulatory preparation"),
      opt("commercial_negotiation", "Commercial negotiation"),
      opt("jv_partnership", "JV / partnership structuring"),
      opt("post_entry_growth", "Post-entry growth support"),
      opt("troubleshooting", "Troubleshooting existing market issues"),
      opt("other", "Other", true),
    ],
  },
  {
    id: "q8_years_experience",
    number: 8,
    label: "How many years of professional experience do you have in this field?",
    type: "single",
    publicOnProfile: true,
    required: true,
    options: [
      opt("1_3", "1–3 years"),
      opt("3_5", "3–5 years"),
      opt("5_10", "5–10 years"),
      opt("10_20", "10–20 years"),
      opt("20_plus", "20+ years"),
    ],
  },
  {
    id: "q9_cross_border",
    number: 9,
    label:
      "Have you supported companies in cross-border business, exports, market entry, partnerships or international growth before?",
    type: "single",
    publicOnProfile: true,
    required: true,
    options: [opt("yes", "Yes"), opt("no", "No"), opt("partially", "Partially")],
  },
  {
    id: "q10_examples",
    number: 10,
    label:
      "Please briefly describe 1–2 relevant examples without sharing confidential details.",
    hint: "Required less than 400 characters",
    type: "short_text",
    maxLength: 400,
    publicOnProfile: false,
    required: true,
  },
  {
    id: "q11_network_access",
    number: 11,
    label: "What kind of network access can you support companies with?",
    hint: "Up to 3 options",
    type: "multiple_max_3",
    publicOnProfile: true,
    required: true,
    options: [
      opt("distributors", "Distributors"),
      opt("manufacturers", "Manufacturers"),
      opt("buyers", "Buyers"),
      opt("suppliers", "Suppliers"),
      opt("government_bodies", "Government Bodies"),
      opt("industry_associations", "Industry Associations"),
      opt("certification_agencies", "Certification Agencies"),
      opt("legal_financial", "Legal / Financial Professionals"),
      opt("investors", "Investors"),
      opt("consultants", "Consultants"),
      opt("logistics_partners", "Logistics Partners"),
      opt("technology_partners", "Technology Partners"),
      opt("project_owners", "Project Owners"),
      opt("other", "Other", true),
    ],
  },
  {
    id: "q12_languages",
    number: 12,
    label: "Which languages can you support business discussions in?",
    hint: "Up to 3 options",
    type: "multiple_max_3",
    publicOnProfile: true,
    required: true,
    options: [
      opt("english", "English"),
      opt("hindi", "Hindi"),
      opt("german", "German"),
      opt("spanish", "Spanish"),
      opt("french", "French"),
      opt("arabic", "Arabic"),
      opt("japanese", "Japanese"),
      opt("korean", "Korean"),
      opt("polish", "Polish"),
      opt("swedish", "Swedish"),
      opt("other", "Other", true),
    ],
  },
  {
    id: "q13_support_preference",
    number: 13,
    label: "How would you prefer to support companies?",
    hint: "Multiple options allowed",
    type: "multiple",
    publicOnProfile: true,
    required: true,
    options: [
      opt("one_time_consultation", "One-time consultation"),
      opt("short_advisory_call", "Short advisory call"),
      opt("market_entry_report", "Market entry report"),
      opt("ongoing_advisory", "Ongoing advisory"),
      opt("partner_identification_support", "Partner identification support"),
      opt("introduction_based", "Introduction-based support"),
      opt("project_based", "Project-based support"),
      opt("retainer_based", "Retainer-based support"),
      opt("other", "Other", true),
    ],
  },
  {
    id: "q14_availability",
    number: 14,
    label: "What is your usual availability for expert requests?",
    type: "single",
    publicOnProfile: true,
    required: true,
    options: [
      opt("24_48_hours", "Available within 24–48 hours"),
      opt("3_5_days", "Available within 3–5 working days"),
      opt("1_week", "Available within 1 week"),
      opt("limited", "Limited availability"),
      opt("case_by_case", "Case-by-case"),
    ],
  },
  {
    id: "q15_documents",
    number: 15,
    label: "What documents can companies share with you for review?",
    hint: "Multiple options allowed",
    type: "multiple",
    publicOnProfile: true,
    required: true,
    options: [
      opt("company_profile", "Company profile"),
      opt("product_catalogue", "Product catalogue"),
      opt("technical_datasheet", "Technical datasheet"),
      opt("export_documents", "Export documents"),
      opt("compliance_documents", "Compliance documents"),
      opt("certifications", "Certifications"),
      opt("commercial_proposal", "Commercial proposal"),
      opt("market_entry_plan", "Market entry plan"),
      opt("distributor_agreement", "Distributor agreement"),
      opt("jv_partnership_proposal", "JV / partnership proposal"),
      opt("other", "Other", true),
    ],
  },
  {
    id: "q16_output_types",
    number: 16,
    label: "What kind of output can you provide to companies?",
    hint: "Up to 3 options",
    type: "multiple_max_3",
    publicOnProfile: true,
    required: true,
    options: [
      opt("advisory_note", "Advisory note"),
      opt("market_feedback", "Market feedback"),
      opt("partner_shortlist", "Partner shortlist"),
      opt("distributor_shortlist", "Distributor shortlist"),
      opt("compliance_summary", "Compliance requirement summary"),
      opt("market_entry_roadmap", "Market entry roadmap"),
      opt("risk_assessment", "Risk assessment"),
      opt("commercial_recommendation", "Commercial recommendation"),
      opt("introduction_support", "Introduction support"),
      opt("detailed_report", "Detailed report"),
      opt("other", "Other", true),
    ],
  },
  {
    id: "q17_company_intents",
    number: 17,
    label: "Which company intents are most relevant for your expertise?",
    hint: "Multiple options allowed",
    type: "multiple",
    publicOnProfile: true,
    required: true,
    options: [
      opt("distributors", "Looking for distributors"),
      opt("buyers", "Looking for buyers"),
      opt("suppliers", "Looking for suppliers"),
      opt("manufacturers", "Looking for manufacturers"),
      opt("technology_partners", "Looking for technology partners"),
      opt("jv_partners", "Looking for JV partners"),
      opt("market_entry_guidance", "Looking for market entry guidance"),
      opt("regulatory_guidance", "Looking for regulatory guidance"),
      opt("investment_finance", "Looking for investment / finance guidance"),
      opt("sourcing_support", "Looking for sourcing support"),
      opt("local_representation", "Looking for local representation"),
      opt("other", "Other", true),
    ],
  },
  {
    id: "q18_company_sizes",
    number: 18,
    label: "What size of companies do you usually support?",
    hint: "Multiple options allowed",
    type: "multiple",
    publicOnProfile: true,
    required: true,
    options: [
      opt("startup", "Startup"),
      opt("msme", "MSME"),
      opt("sme", "SME"),
      opt("mid_sized", "Mid-sized company"),
      opt("large_enterprise", "Large enterprise"),
      opt("any_size", "Any size"),
    ],
  },
  {
    id: "q19_confidentiality",
    number: 19,
    label:
      "Are you comfortable reviewing confidential business information under GTsetu's confidentiality process?",
    type: "single",
    publicOnProfile: true,
    required: true,
    options: [opt("yes", "Yes"), opt("no", "No"), opt("case_by_case", "Case-by-case")],
  },
  {
    id: "q20_services_excluded",
    number: 20,
    label: "Are there any services you do not provide?",
    hint: "Required — short answer (200 characters). Use N/A if none.",
    type: "short_text",
    maxLength: 200,
    publicOnProfile: true,
    required: true,
  },
  {
    id: "q23_exclusions",
    number: 23,
    label: "Are there any sectors, products, or geographies you do not wish to support?",
    hint: "Required — short answer (200 characters). Use N/A if none.",
    type: "short_text",
    maxLength: 200,
    publicOnProfile: true,
    required: true,
  },
  {
    id: EXPERT_ACCOUNT_AGREEMENT_QUESTION_ID,
    number: 24,
    label:
      "I confirm that the information provided is accurate to the best of my knowledge and can be used by GTsetu to create my expert profile and match me with relevant company requests.",
    type: "agreement",
    publicOnProfile: false,
    required: true,
    options: [
      opt(EXPERT_ACCOUNT_AGREEMENT_AGREE_VALUE, "I agree"),
      opt(EXPERT_ACCOUNT_AGREEMENT_DISAGREE_VALUE, "I do not agree"),
    ],
  },
];

export type ExpertAccountExpertiseSectionId = "core" | "engagement" | "availability" | "limitations";

export type ExpertAccountExpertiseSection = {
  id: ExpertAccountExpertiseSectionId;
  number: number;
  title: string;
  questionIds?: string[];
};

export const EXPERT_ACCOUNT_EXPERTISE_SECTIONS: ExpertAccountExpertiseSection[] = [
  {
    id: "core",
    number: 1,
    title: "Core Expertise",
    questionIds: [
      "q1_expertise_types",
      "q4_sectors",
      "q5_services",
      "q2_countries",
      "q3_market_support",
    ],
  },
  {
    id: "engagement",
    number: 2,
    title: "Engagement and Experience",
    questionIds: [
      "q8_years_experience",
      "q9_cross_border",
      "q6_company_types",
      "q7_support_stages",
      "q11_network_access",
      "q12_languages",
      "q17_company_intents",
      "q18_company_sizes",
      "q19_confidentiality",
    ],
  },
  {
    id: "availability",
    number: 3,
    title: "Availability and Deliverables",
    questionIds: [
      "q13_support_preference",
      "q14_availability",
      "q15_documents",
      "q16_output_types",
    ],
  },
  {
    id: "limitations",
    number: 4,
    title: "Limitations",
  },
];

export type ExpertAccountLimitationsDisplayField = {
  label: string;
  questionId?: string;
  staticValue?: string;
};

export const EXPERT_ACCOUNT_PROFILE_LIMITATIONS_FIELDS: ExpertAccountLimitationsDisplayField[] = [
  { label: "Services Not Provided", questionId: "q20_services_excluded" },
  {
    label: "Sectors, Products, or Geographies Not Supported",
    questionId: "q23_exclusions",
  },
];

export const EXPERT_ACCOUNT_LIMITATIONS_DISPLAY_FIELDS: ExpertAccountLimitationsDisplayField[] = [
  ...EXPERT_ACCOUNT_PROFILE_LIMITATIONS_FIELDS,
  { label: "Sectors Not Supported", staticValue: "N/A" },
  { label: "Products Not Supported", staticValue: "N/A" },
  { label: "Geographies Not Supported", staticValue: "N/A" },
  { label: "Additional Notes (Optional)", staticValue: "N/A" },
];

export type ExpertiseSectionRow = {
  label: string;
  displayValues: string[];
  confidential: boolean;
};

type ExpertiseDisplaySource = ExpertPublicExpertiseItem | ExpertAccountQuestionnaireDisplayItem;

function isConfidentialExpertiseItem(
  item: ExpertiseDisplaySource | undefined,
): boolean {
  return item != null && "confidential" in item && item.confidential;
}

function resolveQuestionLabel(
  questionId: string,
  displayMode: "profile" | "questionnaire",
  overrideLabel?: string,
): string {
  const question = EXPERT_ACCOUNT_QUESTIONS.find((q) => q.id === questionId);
  if (!question) return overrideLabel ?? questionId;
  if (displayMode === "profile") {
    return overrideLabel ?? EXPERT_ACCOUNT_PROFILE_FIELD_LABELS[questionId] ?? question.label;
  }
  return question.label;
}

export function buildExpertiseSectionRows(
  expertise: ExpertiseDisplaySource[],
  sectionId: ExpertAccountExpertiseSectionId,
  displayMode: "profile" | "questionnaire",
): ExpertiseSectionRow[] {
  const byQuestionId = new Map(expertise.map((item) => [item.questionId, item]));
  const section = EXPERT_ACCOUNT_EXPERTISE_SECTIONS.find((s) => s.id === sectionId);
  if (!section) return [];

  if (sectionId === "limitations") {
    const limitationFields =
      displayMode === "profile"
        ? EXPERT_ACCOUNT_PROFILE_LIMITATIONS_FIELDS
        : EXPERT_ACCOUNT_LIMITATIONS_DISPLAY_FIELDS;
    const rows: ExpertiseSectionRow[] = limitationFields.map((field) => {
      if (field.staticValue) {
        return { label: field.label, displayValues: [field.staticValue], confidential: false };
      }
      if (!field.questionId) {
        return { label: field.label, displayValues: [], confidential: false };
      }
      const question = EXPERT_ACCOUNT_QUESTIONS.find((q) => q.id === field.questionId);
      if (!question) {
        return { label: field.label, displayValues: [], confidential: false };
      }
      const item = byQuestionId.get(field.questionId);
      return {
        label: resolveQuestionLabel(field.questionId, displayMode, field.label),
        displayValues: item?.displayValues ?? [],
        confidential: isConfidentialExpertiseItem(item),
      };
    });

    if (displayMode === "questionnaire") {
      const agreementItem = byQuestionId.get(EXPERT_ACCOUNT_AGREEMENT_QUESTION_ID);
      if (agreementItem) {
        rows.push({
          label: resolveQuestionLabel(EXPERT_ACCOUNT_AGREEMENT_QUESTION_ID, displayMode),
          displayValues: agreementItem.displayValues,
          confidential: isConfidentialExpertiseItem(agreementItem),
        });
      }
    }

    return displayMode === "profile"
      ? rows.filter((row) => row.displayValues.length > 0)
      : rows;
  }

  const questionIds = section.questionIds ?? [];
  const rows = questionIds.flatMap((questionId) => {
    const question = EXPERT_ACCOUNT_QUESTIONS.find((q) => q.id === questionId);
    if (!question) return [];

    if (displayMode === "profile" && !question.publicOnProfile) return [];

    const item = byQuestionId.get(questionId);
    return [
      {
        label: resolveQuestionLabel(questionId, displayMode),
        displayValues: item?.displayValues ?? [],
        confidential: isConfidentialExpertiseItem(item),
      },
    ];
  });

  return displayMode === "profile"
    ? rows.filter((row) => row.displayValues.length > 0)
    : rows;
}

export type ExpertPublicExpertiseItem = {
  questionId: string;
  number: number;
  /** Full questionnaire wording (admin / internal). */
  label: string;
  /** Short profile field name shown on public expert profiles. */
  fieldLabel: string;
  displayValues: string[];
};

export type ExpertAccountQuestionnaireDisplayItem = ExpertPublicExpertiseItem & {
  confidential: boolean;
};

export function otherTextAnswerKey(questionId: string): string {
  return `${questionId}__other`;
}

export function getQuestionOptionLabel(
  question: ExpertAccountQuestion,
  optionId: string,
  otherText?: string,
): string {
  const option = question.options?.find((o) => o.id === optionId);
  if (!option) return optionId;
  if (optionId === "other" && otherText?.trim()) {
    return `Other: ${otherText.trim()}`;
  }
  return option.label;
}

export function formatAnswerDisplayValues(
  question: ExpertAccountQuestion,
  answers: ExpertAccountAnswers,
): string[] {
  const raw = answers[question.id];
  const otherText = answers[otherTextAnswerKey(question.id)];
  const otherStr = typeof otherText === "string" ? otherText : undefined;

  if (question.type === "countries") {
    return Array.isArray(raw) ? raw.map((c) => String(c).trim()).filter(Boolean) : [];
  }
  if (question.type === "short_text") {
    const text = typeof raw === "string" ? raw.trim() : "";
    return text ? [text] : [];
  }
  if (question.type === "single" || question.type === "agreement") {
    const value = typeof raw === "string" ? raw : "";
    if (!value) return [];
    return [getQuestionOptionLabel(question, value, otherStr)];
  }
  if (question.type === "multiple" || question.type === "multiple_max_3") {
    const ids = Array.isArray(raw) ? raw : [];
    return ids
      .map((id) => getQuestionOptionLabel(question, String(id), otherStr))
      .filter(Boolean);
  }
  return [];
}

export function toExpertPublicExpertise(answers: ExpertAccountAnswers): ExpertPublicExpertiseItem[] {
  return EXPERT_ACCOUNT_QUESTIONS.filter((q) => q.publicOnProfile).flatMap((question) => {
    const displayValues = formatAnswerDisplayValues(question, answers);
    if (displayValues.length === 0) return [];
    return [
      {
        questionId: question.id,
        number: question.number,
        label: question.label,
        fieldLabel: getExpertProfileFieldLabel(question),
        displayValues,
      },
    ];
  });
}

export type ExpertListingCardExpertise = {
  servicesOffered: string[];
  sectorExperience: string[];
  expertiseOffered: string[];
  countriesSupported: string[];
};

export function extractExpertListingCardExpertise(
  answers: ExpertAccountAnswers | null | undefined,
): ExpertListingCardExpertise {
  if (!answers) {
    return {
      servicesOffered: [],
      sectorExperience: [],
      expertiseOffered: [],
      countriesSupported: [],
    };
  }

  const items = toExpertPublicExpertise(answers);
  const byId = new Map(items.map((item) => [item.questionId, item.displayValues]));

  return {
    servicesOffered: byId.get("q5_services") ?? [],
    sectorExperience: byId.get("q4_sectors") ?? [],
    expertiseOffered: byId.get("q1_expertise_types") ?? [],
    countriesSupported: byId.get("q2_countries") ?? [],
  };
}

export function formatExpertListingCardHeading(expertise: ExpertListingCardExpertise, summary?: string | null): string {
  const services = formatExpertiseProfileValue(expertise.servicesOffered);
  if (services) return services;

  const offered = formatExpertiseProfileValue(expertise.expertiseOffered);
  if (offered) return offered;

  const trimmedSummary = summary?.trim();
  if (trimmedSummary) return trimmedSummary;

  return "Verified expert";
}

export function formatExpertListingCardLocation(
  expertise: ExpertListingCardExpertise,
  expertCountry?: string | null,
  expertTargetCountry?: string | null,
): string {
  const supported = expertise.countriesSupported.filter(Boolean);
  if (supported.length > 0) return supported.slice(0, 2).join(" · ");

  const from = expertCountry?.trim();
  const to = expertTargetCountry?.trim();
  if (from && to && from.toLowerCase() !== to.toLowerCase()) return `${from} ↔ ${to}`;
  if (from) return from;
  if (to) return to;
  return "";
}

export function toExpertAccountQuestionnaireDisplay(
  answers: ExpertAccountAnswers,
): ExpertAccountQuestionnaireDisplayItem[] {
  return EXPERT_ACCOUNT_QUESTIONS.flatMap((question) => {
    const displayValues = formatAnswerDisplayValues(question, answers);
    if (displayValues.length === 0) return [];
    return [
      {
        questionId: question.id,
        number: question.number,
        label: question.label,
        fieldLabel: getExpertProfileFieldLabel(question),
        displayValues,
        confidential: !question.publicOnProfile,
      },
    ];
  });
}

export type ExpertAccountValidationResult =
  | { ok: true; answers: ExpertAccountAnswers }
  | { ok: false; message: string; fieldId?: string };

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((v) => String(v).trim()).filter(Boolean);
}

export function validateExpertAccountAnswers(
  raw: unknown,
  options?: { requireAgreement?: boolean },
): ExpertAccountValidationResult {
  if (!raw || typeof raw !== "object") {
    return { ok: false, message: "Answers are required." };
  }
  const input = raw as ExpertAccountAnswers;
  const normalized: ExpertAccountAnswers = {};

  for (const question of EXPERT_ACCOUNT_QUESTIONS) {
    const value = input[question.id];
    const otherKey = otherTextAnswerKey(question.id);
    const otherRaw = input[otherKey];
    if (typeof otherRaw === "string" && otherRaw.trim()) {
      normalized[otherKey] = otherRaw.trim();
    }

    if (question.type === "countries") {
      const countries = asStringArray(value);
      if (question.required && countries.length === 0) {
        return { ok: false, message: `Question ${question.number} is required.`, fieldId: question.id };
      }
      const max = question.maxCountries ?? 5;
      if (countries.length > max) {
        return {
          ok: false,
          message: `Question ${question.number}: select at most ${max} countries or regions.`,
          fieldId: question.id,
        };
      }
      if (countries.length > 0) normalized[question.id] = countries;
      continue;
    }

    if (question.type === "short_text") {
      const text = typeof value === "string" ? value.trim() : "";
      if (question.required && !text) {
        return { ok: false, message: `Question ${question.number} is required.`, fieldId: question.id };
      }
      if (text) {
        const maxLen = question.maxLength ?? 400;
        if (text.length > maxLen) {
          return {
            ok: false,
            message: `Question ${question.number} must be ${maxLen} characters or fewer.`,
            fieldId: question.id,
          };
        }
        normalized[question.id] = text;
      }
      continue;
    }

    if (question.type === "single" || question.type === "agreement") {
      const selected = typeof value === "string" ? value.trim() : "";
      if (question.required && !selected) {
        return { ok: false, message: `Question ${question.number} is required.`, fieldId: question.id };
      }
      if (selected) {
        const validIds = new Set((question.options ?? []).map((o) => o.id));
        if (!validIds.has(selected)) {
          return { ok: false, message: `Question ${question.number}: invalid selection.`, fieldId: question.id };
        }
        normalized[question.id] = selected;
      }
      continue;
    }

    if (question.type === "multiple" || question.type === "multiple_max_3") {
      const selected = asStringArray(value);
      if (question.required && selected.length === 0) {
        return { ok: false, message: `Question ${question.number} is required.`, fieldId: question.id };
      }
      const validIds = new Set((question.options ?? []).map((o) => o.id));
      for (const id of selected) {
        if (!validIds.has(id)) {
          return { ok: false, message: `Question ${question.number}: invalid selection.`, fieldId: question.id };
        }
      }
      if (question.type === "multiple_max_3" && selected.length > 3) {
        return {
          ok: false,
          message: `Question ${question.number}: select at most 3 options.`,
          fieldId: question.id,
        };
      }
      if (selected.includes("other")) {
        const otherText = typeof otherRaw === "string" ? otherRaw.trim() : "";
        if (!otherText) {
          return {
            ok: false,
            message: `Question ${question.number}: please specify your "Other" answer.`,
            fieldId: otherKey,
          };
        }
      }
      if (selected.length > 0) normalized[question.id] = selected;
    }
  }

  const agreement = normalized[EXPERT_ACCOUNT_AGREEMENT_QUESTION_ID];
  if (options?.requireAgreement !== false) {
    if (agreement !== EXPERT_ACCOUNT_AGREEMENT_AGREE_VALUE) {
      return {
        ok: false,
        message: 'You must select "I agree" to complete your expert account questionnaire.',
        fieldId: EXPERT_ACCOUNT_AGREEMENT_QUESTION_ID,
      };
    }
  }

  return { ok: true, answers: normalized };
}
